import Papa from "papaparse";

/**
 * Causal-Loop-only data preparation. Neither function is called by, or
 * changes anything about, the standalone Bid Optimizer upload flow
 * (StepThree.tsx) or the standalone Geo Lift upload flow (PreCampaign.tsx) -
 * those keep uploading their own file exactly as they always have.
 *
 * Causal Loop shares ONE raw file between both engines, but the two engines
 * need different shapes of it:
 *   - Bid Optimizer wants every segment row as-is (age/gender/device
 *     combinations per location per date) to compute per-segment multipliers.
 *   - Geo Lift has always assumed exactly one row per location per date -
 *     true for its own standalone uploads, but not true for Causal Loop's
 *     shared file. That mismatch doesn't error cleanly; it silently produces
 *     wrong period counts deep inside GeoLift's existing code (see
 *     causal-loop-feature/plan.md - e.g. treatment_periods computed as
 *     "total rows / num locations", which only equals "periods per location"
 *     when there's one row per location per date).
 *
 * Rather than patching GeoLift's own code (large, unfamiliar, multiple
 * instances of the same assumption already found in two places), each engine
 * gets its own extraction function producing the shape it has always
 * expected, from the one uploaded file.
 */

/** Bid Optimizer wants the full segment-level file unchanged - this is an
 * identity function today, kept as an explicit named seam (matching
 * extractSyntheticControlInput) rather than skipping straight to the raw
 * File object, in case Bid Optimizer ever needs its own transformation later. */
export function extractBidOptimizerInput(file: File): File {
  return file;
}

/** Finds an ad-spend-like column in the raw file's headers, if one exists -
 * checked by name (exact "adspend" match first, then a looser
 * ad[-_ ]?spend pattern), not by position or column count. The backend's
 * _apply_column_mappings preserves any column not matched by date/location/
 * kpi, and separately checks "adspend" in df.columns downstream - so naming
 * this column literally "adspend" in the output is what makes it recognized,
 * regardless of how many total columns the aggregated file ends up with. */
function findAdSpendColumn(headers: string[]): string | undefined {
  const exact = headers.find((h) => h.trim().toLowerCase() === "adspend");
  if (exact) return exact;
  return headers.find((h) => /ad[\s_-]?spend/i.test(h.trim()));
}

/** Collapses the shared file to exactly one row per (date, location),
 * summing the target/KPI column (and, if present, an ad-spend column) across
 * whatever segment rows existed for that date+location - restoring the shape
 * GeoLift's existing code has always required, computed entirely client-side
 * so no backend/shared code needs to change. */
export function extractSyntheticControlInput(
  file: File,
  dateColumn: string,
  locationColumn: string,
  targetColumn: string
): Promise<File> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        try {
          const headers = results.meta.fields || [];
          const adSpendColumn = findAdSpendColumn(headers);

          const grouped = new Map<
            string,
            { date: string; location: string; target: number; adSpend: number }
          >();
          (results.data as any[]).forEach((row) => {
            if (!row) return;
            const date = row[dateColumn];
            const location = row[locationColumn];
            if (date === undefined || date === null || date === "" || location === undefined || location === null || location === "") {
              return;
            }
            const key = `${date}|||${location}`;
            const targetVal = parseFloat(row[targetColumn]) || 0;
            const adSpendVal = adSpendColumn ? parseFloat(row[adSpendColumn]) || 0 : 0;
            const existing = grouped.get(key);
            if (existing) {
              existing.target += targetVal;
              existing.adSpend += adSpendVal;
            } else {
              grouped.set(key, { date: String(date), location: String(location), target: targetVal, adSpend: adSpendVal });
            }
          });

          const aggregatedRows = Array.from(grouped.values()).map((r) => {
            const out: Record<string, string | number> = {
              [dateColumn]: r.date,
              [locationColumn]: r.location,
              [targetColumn]: r.target,
            };
            if (adSpendColumn) {
              out["adspend"] = r.adSpend;
            }
            return out;
          });

          const csv = Papa.unparse(aggregatedRows);
          resolve(new File([csv], "geo_lift_data.csv", { type: "text/csv" }));
        } catch (err) {
          reject(err);
        }
      },
      error: (err) => reject(err),
    });
  });
}
